/**
 * Sending work forward and backward within a layer (SRS C8).
 *
 * Objects are painted in `zIndex` order, so re-ordering them is arithmetic on that
 * one number. Only the objects that actually move are handed back: renumbering a
 * whole page would put every object on the board into one step of undo and send the
 * lot to everybody else, for a change that moved a single shape.
 *
 * Ordering is always WITHIN a layer. Nothing here can lift a shape out of the lesson
 * and onto the teacher-only layer, or push one down out of it.
 */

import type { BoardObject } from '@opal/shared';

/** SRS C8 — the four ways work can be re-ordered. */
export type ZMove = 'front' | 'forward' | 'backward' | 'back';

/** Objects on the same layer as this one, in the order they are painted. */
function siblings(all: readonly BoardObject[], layer: BoardObject['layer']): BoardObject[] {
  return all
    .filter((object) => object.layer === layer)
    .sort((a, b) => a.zIndex - b.zIndex || a.createdAt - b.createdAt);
}

/** The same object, sitting at a different height in the stack. */
function at(object: BoardObject, zIndex: number): BoardObject {
  return { ...object, zIndex, updatedAt: Date.now() };
}

/** Straight to the top of its layer, or straight to the bottom. */
function toEnd(
  all: readonly BoardObject[],
  chosen: readonly BoardObject[],
  move: 'front' | 'back',
): BoardObject[] {
  const moved: BoardObject[] = [];
  for (const object of chosen) {
    const others = siblings(all, object.layer).filter((other) => other.id !== object.id);
    const first = others[0];
    const last = others[others.length - 1];
    if (!first || !last) continue;
    const zIndex = move === 'front' ? last.zIndex + 1 : first.zIndex - 1;
    if (zIndex !== object.zIndex) moved.push(at(object, zIndex));
  }
  return moved;
}

/** One place up, or one place down: swap heights with the neighbour that way. */
function byOne(
  all: readonly BoardObject[],
  object: BoardObject,
  move: 'forward' | 'backward',
): BoardObject[] {
  const stack = siblings(all, object.layer);
  const index = stack.findIndex((other) => other.id === object.id);
  const neighbour = stack[move === 'forward' ? index + 1 : index - 1];
  if (index < 0 || !neighbour) return [];
  // Two objects placed in the same millisecond can share a height, and swapping two
  // equal numbers moves nothing. Stepping past the neighbour does.
  const zIndex =
    neighbour.zIndex === object.zIndex
      ? neighbour.zIndex + (move === 'forward' ? 1 : -1)
      : neighbour.zIndex;
  return [at(object, zIndex), at(neighbour, object.zIndex)];
}

/**
 * The objects that move when a selection is sent forward or back, already re-numbered.
 *
 * Given in the order they should be written. An object named that is not on the board,
 * or a move that would change nothing, simply produces nothing.
 */
export function reordered(
  all: readonly BoardObject[],
  chosenIds: readonly string[],
  move: ZMove,
): readonly BoardObject[] {
  const ids = new Set(chosenIds);
  const chosen = siblings(all, 'content')
    .concat(siblings(all, 'teacher'))
    .filter((object) => ids.has(object.id));
  if (chosen.length === 0) return [];

  if (move === 'front' || move === 'back') return toEnd(all, chosen, move);

  // One at a time, starting from the end the work is moving towards, so a pair moved
  // together does not have the two of them swapping with each other.
  const ordered = move === 'forward' ? [...chosen].reverse() : chosen;
  const moved = new Map<string, BoardObject>();
  let board = [...all];
  for (const object of ordered) {
    const current = board.find((other) => other.id === object.id) ?? object;
    for (const changed of byOne(board, current, move)) {
      moved.set(changed.id, changed);
      board = board.map((other) => (other.id === changed.id ? changed : other));
    }
  }
  return [...moved.values()];
}
